import React, { useEffect, useMemo, useRef } from "react";
import { Empty, Tooltip } from "antd";
import * as echarts from "echarts";
import { formatCurrency, formatCurrency2 } from "../../../utils/Utils";
import { InfoCircleOutlined } from "@ant-design/icons";

const currency = "INR";

interface BudgetVsSpendChartProps {
  budgetVsSpendData: any;
}

const ZERO_SUMMARY = {
  TotalBudgetValue: "0.00",
  TotalBudgetPercentage: "0.00",
  ActualSpendValue: "0.00",
  ActualSpendPercentage: "0.00",
  RemainingBudgetValue: "0.00",
  RemainingBudgetPercentage: "0.00",
  SpendVariationValue: "0.00",
  SpendVariationPercentage: "0.00",
};

const BudgetVsSpendChart: React.FC<BudgetVsSpendChartProps> = ({
  budgetVsSpendData,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // Derive everything from props with useMemo. No internal data/loading
  // state — the parent owns loading. Keeping derivation pure prevents the
  // chart container from unmounting/remounting (which orphaned the echarts
  // instance and caused the "renders then shows No data" flicker).
  const summaryData = useMemo(() => {
    if (
      !budgetVsSpendData ||
      budgetVsSpendData?.totalBudget?.value === undefined
    ) {
      return ZERO_SUMMARY;
    }
    return {
      TotalBudgetValue: budgetVsSpendData?.totalBudget?.value ?? "0.00",
      TotalBudgetPercentage:
        budgetVsSpendData?.totalBudget?.percentage ?? "0.00",
      ActualSpendValue: budgetVsSpendData?.actualSpend?.value ?? "0.00",
      ActualSpendPercentage:
        budgetVsSpendData?.actualSpend?.percentage ?? "0.00",
      RemainingBudgetValue:
        budgetVsSpendData?.remainingBudget?.value ?? "0.00",
      RemainingBudgetPercentage:
        budgetVsSpendData?.remainingBudget?.percentage ?? "0.00",
      SpendVariationValue: budgetVsSpendData?.spendVariation?.value ?? "0.00",
      SpendVariationPercentage:
        budgetVsSpendData?.spendVariation?.percentage ?? "0.00",
    };
  }, [budgetVsSpendData]);

  const chartData = useMemo(() => {
    const rows: any[] = budgetVsSpendData?.monthOnMonthSavings?.data ?? [];
    const dates = rows.map((row: any) => `${row.month} ${row.year}`);
    const budgetAllocated = rows.map((row: any) =>
      Number(row.totalBudgetValue ?? 0)
    );
    const amountSpent = rows.map((row: any) =>
      Number(row.totalSpendValue ?? 0)
    );
    const hasNonZero = [...budgetAllocated, ...amountSpent].some(
      (v) => Number(v) !== 0
    );
    return {
      dates,
      budgetAllocated,
      amountSpent,
      hasChartData: dates.length > 0 && hasNonZero,
    };
  }, [budgetVsSpendData]);

  // Render / update the chart whenever derived data changes.
  useEffect(() => {
    if (!chartRef.current || !chartData.hasChartData) {
      return;
    }

    // Defensive: if a previously-created instance is bound to a different
    // (or detached) DOM node, dispose it before re-init.
    if (
      chartInstance.current &&
      chartInstance.current.getDom() !== chartRef.current
    ) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    chartInstance.current.setOption(
      {
        tooltip: {
          trigger: "axis",
          formatter: (params: any) => {
            const budget = params[0].value;
            const spent = params[1].value;
            const percentage =
              spent !== 0 && ((spent / budget) * 100)?.toFixed(2);
            const color = Number(spent) > Number(budget) ? "red" : "green";
            return `
              <div>
                <strong>${params[0].name}</strong><br/>
                Budget: ${formatCurrency(budget, currency)}<br/>
                Spent: ${formatCurrency(spent, currency)} <span style="color:${color};">(${
              percentage === "NaN" || percentage === false ? "0.00" : percentage
            }%)</span>
              </div>
            `;
          },
        },
        grid: {
          left: "3%",
          right: "0",
          bottom: "15%",
          top: "8%",
          containLabel: true,
        },
        legend: {
          data: ["Budget Allocated", "Amount Spent"],
          orient: "horizontal",
          bottom: "5%",
          right: "0",
        },
        xAxis: {
          type: "category",
          data: chartData.dates,
          name: "Date",
          nameLocation: "middle",
          nameTextStyle: { fontWeight: "bold", fontSize: 14, padding: 20 },
        },
        yAxis: {
          type: "value",
          name: `Amount (${currency})`,
          nameLocation: "middle",
          axisLabel: {
            formatter: (value: number) => formatCurrency2(value, currency),
          },
          nameTextStyle: { fontWeight: "bold", fontSize: 14, padding: 50 },
        },
        series: [
          {
            name: "Budget Allocated",
            type: "line",
            data: chartData.budgetAllocated,
            lineStyle: { color: "#3BAEA3" },
            smooth: true,
            areaStyle: { color: "rgba(59, 174, 163, 0.3)" },
          },
          {
            name: "Amount Spent",
            type: "line",
            data: chartData.amountSpent,
            lineStyle: { color: "#5470C6" },
            smooth: true,
          },
        ],
      },
      true
    );

    // Resize on the next frame so the surrounding layout has fully settled
    // (e.g. after a parent overlay is removed).
    const raf = requestAnimationFrame(() => {
      chartInstance.current?.resize();
    });
    return () => cancelAnimationFrame(raf);
  }, [chartData]);

  // Keep the chart sized to its container at all times. Without this the
  // chart appears compressed/squashed when the layout reflows (sidebar
  // toggle, filters overlay appearing/disappearing, window resize, etc.).
  // resize() is deferred to rAF and de-duped to avoid the
  // "ResizeObserver loop completed with undelivered notifications" error
  // that fires when echarts triggers layout from inside the RO callback.
  useEffect(() => {
    let raf = 0;
    const handleResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        chartInstance.current?.resize();
      });
    };
    window.addEventListener("resize", handleResize);

    let ro: ResizeObserver | null = null;
    if (chartRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(handleResize);
      ro.observe(chartRef.current);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      ro?.disconnect();
    };
  }, []);

  // Dispose chart instance on unmount.
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div>
      <div
        className="card-title-group mb-1"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="card-title">
          <h6 className="title">Budget vs Spend Analysis</h6>
          <p>
            Comparison of budget allocation and spending over the selected
            period.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="analytic-data-group analytic-ov-group g-3 mt-1">
        <div className="analytic-data analytic-ov-data blackFont">
          <div className="title">Allocated Budget</div>
          <div className="amount">
            {formatCurrency2(Number(summaryData.TotalBudgetValue), currency)}
            <Tooltip
              title={formatCurrency(summaryData.TotalBudgetValue, "INR")}
            >
              <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 12 }} />
            </Tooltip>
          </div>
          <div className="change">{summaryData.TotalBudgetPercentage}%</div>
        </div>
        <div className="analytic-data analytic-ov-data">
          <div className="title">Actual Spend</div>
          <div className="amount">
            {formatCurrency2(Number(summaryData.ActualSpendValue), currency)}
            <Tooltip
              title={formatCurrency(summaryData.ActualSpendValue, "INR")}
            >
              <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 12 }} />
            </Tooltip>
          </div>
          <div className="change">{summaryData.ActualSpendPercentage}%</div>
        </div>
        <div className="analytic-data analytic-ov-data">
          <div className="title">Remaining Budget</div>
          <div className="amount">
            {formatCurrency2(
              Number(summaryData.RemainingBudgetValue),
              currency
            )}
            <Tooltip
              title={formatCurrency(summaryData.RemainingBudgetValue, "INR")}
            >
              <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 12 }} />
            </Tooltip>
          </div>
          <div className="change">
            {summaryData.RemainingBudgetPercentage}%
          </div>
        </div>
        <div className="analytic-data analytic-ov-data">
          <div className="title">Spend Overrun %</div>
          <div className="amount">
            {formatCurrency2(
              Number(summaryData.SpendVariationValue),
              currency
            )}
          </div>
          <div>
            {summaryData.SpendVariationPercentage !== undefined &&
            summaryData.SpendVariationPercentage !== null
              ? `${Number(summaryData.SpendVariationPercentage).toFixed(2)}%`
              : "0%"}
          </div>
        </div>
      </div>

      {/* Chart container — ALWAYS rendered with a fixed height so echarts
          can measure it correctly. The "No data" Empty is overlaid on top
          (visibility toggle, not display) so we never tear the chart out
          of the DOM and orphan the echarts instance. */}
      <div style={{ position: "relative", minHeight: "350px" }}>
        <div
          ref={chartRef}
          id="budget-spend-chart"
          style={{
            width: "100%",
            height: "350px",
            visibility: chartData.hasChartData ? "visible" : "hidden",
          }}
        />
        {!chartData.hasChartData && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Empty description="No data available to generate or display the graph." />
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetVsSpendChart;
